// src/features/executive/insight/widgets/RetentionRateTile.tsx
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import KPICard from 'features/executive/components/KPICard/KPICard'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'
import { formatPercent } from 'utils/executive.utils'
import { useRetention } from '../hooks/use-retention'

// Statistical rule-of-thumb minimum for stable proportion estimates. Below
// this, a 5-patient swing can move the headline rate by 10+ pp — flag the
// caveat so execs don't over-interpret small-cohort noise.
const SMALL_COHORT_THRESHOLD = 30

// Absolute-level thresholds for the actionable nudge chip. < 25% on a
// dental-clinic intra-window repeat rate is unusually low and suggests a
// recall/follow-up gap.
const RATE_LOW_PCT = 25

// Percentage-point shift threshold for the trend chip — small fluctuations
// in a rate that already lives in the 30–50% range are noise.
const SHIFT_MIN_PP = 5

const colorFor = (rate: number): 'success' | 'warning' | 'error' => {
  if (rate >= 50) return 'success'
  if (rate >= 30) return 'warning'
  return 'error'
}

const fmt = (n: number) => new Intl.NumberFormat('th-TH').format(Math.round(n || 0))

export default function RetentionRateTile() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useRetention()

  if (loading) return <LoadingSkeleton height={160} />
  if (error) return <ErrorState onRetry={refetch} />
  if (!data || data.totalPatients === 0) {
    return (
      <EmptyState
        message={t(
          'EXECUTIVE.INSIGHT.RETENTION.EMPTY',
          'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
        )}
      />
    )
  }

  const rate = data.retentionRate
  const previousRate = data.previousRate
  const previousRepeat = data.previousRepeatPatients ?? null
  const previousTotal = data.previousTotalPatients ?? null
  const shiftPp = data.rateShiftPp ?? null
  const smallCohort = data.totalPatients < SMALL_COHORT_THRESHOLD

  const showShiftUp = shiftPp != null && shiftPp >= SHIFT_MIN_PP
  const showShiftDown = shiftPp != null && shiftPp <= -SHIFT_MIN_PP
  const showLow = rate < RATE_LOW_PCT

  const tooltipIcon = (
    <Tooltip
      title={t(
        'EXECUTIVE.INSIGHT.RETENTION.SOURCE_TOOLTIP',
        'Retention Rate = (คนไข้ที่มาอย่างน้อย 2 วันต่างกันในช่วงนั้น) ÷ (คนไข้ทั้งหมดใน cohort) × 100\n\nนี่คือ "intra-window repeat rate" — วัดความถี่ภายในช่วงเดียวเท่านั้น\nต่างจาก classical cohort retention (acquired ใน period X → กลับมาใน period Y)\n\nสำหรับ TDH:\n• ≥ 50% = แข็งแกร่ง (TX ส่วนใหญ่ต้องการ ≥ 2 visit)\n• 30–50% = ปกติ\n• < 30% = ควรพิจารณา recall/follow-up program'
      )}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 400 } } }}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
    </Tooltip>
  )

  return (
    <KPICard
      title={t('EXECUTIVE.INSIGHT.RETENTION.TITLE')}
      value={formatPercent(rate, 1)}
      color={colorFor(rate)}
      headerAction={tooltipIcon}
      caption={
        <Box>
          {/* Current period raw counts */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('EXECUTIVE.INSIGHT.RETENTION.CAPTION', {
              repeat: fmt(data.repeatPatients),
              total: fmt(data.totalPatients),
            })}
          </Typography>

          {/* Prev period raw counts for direct comparison */}
          {previousRate != null && previousRepeat != null && previousTotal != null && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              {t(
                'EXECUTIVE.INSIGHT.RETENTION.PREV_CAPTION',
                'ก่อนหน้า: {{repeat}}/{{total}} ({{rate}}%)',
                {
                  repeat: fmt(previousRepeat),
                  total: fmt(previousTotal),
                  rate: previousRate.toFixed(1),
                }
              )}
            </Typography>
          )}

          {/* Chips stack: small-cohort + level + shift */}
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
            {smallCohort && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.RETENTION.SMALL_COHORT',
                      'กลุ่มตัวอย่างน้อย ({{count}} คน) — อัตราอาจยังไม่เสถียร',
                      { count: fmt(data.totalPatients) } as any
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showLow && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.RETENTION.LOW_LEVEL',
                      'อัตราการกลับมาใช้บริการต่ำ — แนะนำให้ติดตาม/โทรนัดผู้ป่วยกลับมา (recall)'
                    )}
                  </Typography>
                }
                color="error"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showShiftUp && (
              <Chip
                icon={<TrendingUpIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.RETENTION.SHIFT_UP',
                      'อัตราการกลับมาเพิ่มขึ้น +{{pp}}pp ({{prev}}% → {{cur}}%)',
                      {
                        pp: shiftPp!.toFixed(1),
                        prev: (previousRate ?? 0).toFixed(1),
                        cur: rate.toFixed(1),
                      }
                    )}
                  </Typography>
                }
                color="success"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showShiftDown && (
              <Chip
                icon={<TrendingDownIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.RETENTION.SHIFT_DOWN',
                      'อัตราการกลับมาลดลง {{pp}}pp ({{prev}}% → {{cur}}%)',
                      {
                        pp: shiftPp!.toFixed(1),
                        prev: (previousRate ?? 0).toFixed(1),
                        cur: rate.toFixed(1),
                      }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
          </Stack>
        </Box>
      }
    />
  )
}
